import { template } from 'lodash-es';
import * as angular from 'ng-file-upload';

import * as Common from '../Common/modules';
import * as Models from '../Models';

export interface IDocumentApiClient {
  getSolarDocumentTypes(
    igsCustomerNumber: string,
  ): ng.IPromise<Models.DocumentManagement.SolarDocumentType.ISolarDocumentType[]>;
  getSolarDocumentTypeByCode(
    documentTypeCode: string,
  ): ng.IPromise<Models.DocumentManagement.SolarDocumentType.ISolarDocumentType>;
  getCustomerDocuments(igsCustomerNumber: string): ng.IPromise<Models.DocumentManagement.Document.IDocument[]>;
  getIssueDocuments(issueId: number): ng.IPromise<Models.DocumentManagement.Document.IDocument[]>;
  getDocumentForDownload(
    documentGuid: string,
  ): ng.IPromise<Models.DocumentManagement.DocumentDownload.IDocumentDownload>;
  uploadCustomerDocument(
    customerDocumentUploadModel: Models.DocumentManagement.CustomerDocumentUploadModel.IDocumentUploadModel,
  ): angular.angularFileUpload.IUploadPromise<any>;
  uploadIssueDocument(
    issueDocumentUploadModel: Models.DocumentManagement.CustomerDocumentUploadModel.IssueDocumentUploadModel,
  ): angular.angularFileUpload.IUploadPromise<any>;
  archiveDocument(
    archiveCustomerDocumentRequest: Models.DocumentManagement.ArchiveDocumentRequest.IArchiveDocumentRequest,
  ): ng.IPromise<Models.DocumentManagement.Document.IDocument[]>;
  archiveIssueDocument(
    archiveCustomerDocumentRequest: Models.DocumentManagement.ArchiveDocumentRequest.IArchiveDocumentRequest,
  ): ng.IPromise<Models.DocumentManagement.Document.IDocument[]>;
  updateDocumentFileName(
    unarchiveCustomerDocumentRequest: Models.DocumentManagement.ArchiveDocumentRequest.IArchiveDocumentRequest,
  ): ng.IPromise<Models.DocumentManagement.Document.IDocument[]>;
  getDocumentTemplateForDownload(
    templateCode: string,
  ): ng.IPromise<Models.DocumentManagement.DocumentDownload.DocumentDownload>;
}

export class DocumentApiClient implements IDocumentApiClient {
  public static $inject = ["igs.apiClientService", "Upload"];

  public prefix: string;

  constructor(
    private igsApiClientService: Common.ApiClientService.Igs.Common.IApiClientService,
    private uploadService: angular.angularFileUpload.IUploadService,
  ) {
    this.prefix = "Document";
  }

  public getSolarDocumentTypes = (
    igsCustomerNumber: string,
  ): ng.IPromise<Models.DocumentManagement.SolarDocumentType.ISolarDocumentType[]> => {
    var url = `${this.prefix}/GetSolarDocumentTypes/${igsCustomerNumber}`;
    return this.igsApiClientService.get(url);
  };

  public getSolarDocumentTypeByCode = (
    documentTypeCode: string,
  ): ng.IPromise<Models.DocumentManagement.SolarDocumentType.ISolarDocumentType> => {
    var url = `${this.prefix}/GetSolarDocumentType/${documentTypeCode}`;
    return this.igsApiClientService.get(url);
  };

  public getCustomerDocuments = (
    igsCustomerNumber: string,
  ): ng.IPromise<Models.DocumentManagement.Document.IDocument[]> => {
    var url = `${this.prefix}/GetCustomerDocuments/${igsCustomerNumber}?includeArchived=true`;
    return this.igsApiClientService.get(url);
  };

  public getIssueDocuments = (issueId: number): ng.IPromise<Models.DocumentManagement.Document.IDocument[]> => {
    var url = `${this.prefix}/GetIssueDocuments/` + issueId;
    return this.igsApiClientService.get(url);
  };

  public getDocumentForDownload = (
    documentGuid: string,
  ): ng.IPromise<Models.DocumentManagement.DocumentDownload.DocumentDownload> => {
    var url = `${this.prefix}/GetDocumentForDownload/` + documentGuid;
    return this.igsApiClientService.get(url);
  };

  public uploadCustomerDocument = (
    customerDocumentUploadModel: Models.DocumentManagement.CustomerDocumentUploadModel.CustomerDocumentUploadModel,
  ): ng.IPromise<any> => {
    var url = `api/${this.prefix}/UploadCustomerDocument`;

    var fileNameEnd = customerDocumentUploadModel.file.name.lastIndexOf(".");
    var fileExtensionNoDot = customerDocumentUploadModel.file.name.substring(fileNameEnd + 1);

    return this.uploadService.upload({
      url: url,
      method: "POST",
      data: {
        file: customerDocumentUploadModel.file,
        fileName: customerDocumentUploadModel.userInputFileName,
        fileExtension: fileExtensionNoDot,
        documentTypeCode: customerDocumentUploadModel.documentType.code,
        contentType: customerDocumentUploadModel.file.type,
        igsCustomerNumber: customerDocumentUploadModel.key,
      },
    });
  };

  public uploadIssueDocument = (
    issueDocumentUploadModel: Models.DocumentManagement.CustomerDocumentUploadModel.IssueDocumentUploadModel,
  ): angular.angularFileUpload.IUploadPromise<any> => {
    var url = `api/${this.prefix}/UploadIssueDocument`;

    var fileNameEnd = issueDocumentUploadModel.file.name.lastIndexOf(".");
    var fileExtensionNoDot = issueDocumentUploadModel.file.name.substring(fileNameEnd + 1);

    return this.uploadService.upload({
      url: url,
      method: "POST",
      data: {
        file: issueDocumentUploadModel.file,
        fileName: issueDocumentUploadModel.userInputFileName,
        fileExtension: fileExtensionNoDot,
        contentType: issueDocumentUploadModel.file.type,
        issueId: issueDocumentUploadModel.key,
      },
    });
  };

  public archiveDocument = (
    archiveDocumentRequest: Models.DocumentManagement.ArchiveDocumentRequest.IArchiveDocumentRequest,
  ): ng.IPromise<Models.DocumentManagement.Document.IDocument[]> => {
    var url =
      archiveDocumentRequest.type === "customer"
        ? `${this.prefix}/ArchiveCustomerDocument`
        : `${this.prefix}/ArchiveIssueDocument`;
    return this.igsApiClientService.post(url, archiveDocumentRequest);
  };

  public archiveIssueDocument = (
    archiveDocumentRequest: Models.DocumentManagement.ArchiveDocumentRequest.IArchiveDocumentRequest,
  ): ng.IPromise<Models.DocumentManagement.Document.IDocument[]> => {
    var url = `${this.prefix}/ArchiveIssueDocument`;
    return this.igsApiClientService.post(url, archiveDocumentRequest);
  };

  public updateDocumentFileName = (
    updateDocumentFileNameRequest: Models.DocumentManagement.UpdateDocumentFileNameRequest.IUpdateDocumentFileNameRequest,
  ): ng.IPromise<Models.DocumentManagement.Document.IDocument[]> => {
    var url =
      updateDocumentFileNameRequest.type === "customer"
        ? `${this.prefix}/UpdateCustomerDocumentFileName`
        : `${this.prefix}/UpdateIssueDocumentFileName`;
    return this.igsApiClientService.post(url, updateDocumentFileNameRequest);
  };

  public getDocumentTemplateForDownload = (
    templateCode: string,
  ): ng.IPromise<Models.DocumentManagement.DocumentDownload.DocumentDownload> => {
    var url = `${this.prefix}/DocumentTemplates/${templateCode}`;
    return this.igsApiClientService.get(url);
  };
}
